/**
 * Audits tab — read-only browse of stored AuditPackages (audit-ingest spec §7).
 *
 * A filterable list of envelope rows, with a click-through drill-in that renders
 * the package body as pretty-printed JSON inside a COLLAPSED <details>. The
 * console interprets NOTHING inside a package: no charts over package internals,
 * no per-step summaries, no derived verdicts. Browse means browse.
 *
 * Transcript refs inside a package are inert text here — they resolve only on
 * the originating appliance, and the drill-in says so rather than rendering them
 * as something a reader might expect to click.
 *
 * Consistent with RunsTab / BudgetsTab: local `useState`, no router, Saga-themed
 * classes from index.css. There is no mutation on this plane.
 */

import { useState } from 'react';
import {
  useAudits, useAuditDetail,
  type AuditEnvelopeView, type AuditFilters,
} from '../lib/queries';

/** Why a transcript reference inside a package cannot be followed from here. */
const TRANSCRIPT_REF_NOTE =
  'A transcript reference inside a package is an opaque identifier: it resolves '
  + 'only on the originating appliance. Watchtower stores it and never follows it.';

/** Grouped byte count for the envelope's `size_bytes`. */
export function fmtBytes(n: number): string {
  return `${n.toLocaleString()} B`;
}

// ---------------------------------------------------------------------------
// Drill-in — the envelope field grid, the package JSON, the transcript note.
// ---------------------------------------------------------------------------

function AuditDetail({ packageId, onBack }: { packageId: string; onBack: () => void }) {
  const { data, isLoading, isError, error } = useAuditDetail(packageId);
  const envelope: AuditEnvelopeView | undefined = data?.envelope;

  return (
    <section aria-label={`Audit package ${packageId}`}>
      <button type="button" className="btn" onClick={onBack}>
        ← Back to audits
      </button>

      {isLoading ? (
        <p className="note">Loading audit package…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load audit package:{' '}
          {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : !envelope ? (
        <p className="note">This audit package is no longer available.</p>
      ) : (
        <>
          <dl className="audit-detail-meta">
            <div className="meta-row">
              <dt>package</dt>
              <dd className="mono">{envelope.package_id}</dd>
            </div>
            <div className="meta-row">
              <dt>run</dt>
              <dd className="mono">{envelope.run_id}</dd>
            </div>
            <div className="meta-row">
              <dt>platform</dt>
              <dd>{envelope.platform}</dd>
            </div>
            <div className="meta-row">
              <dt>agent</dt>
              <dd>{envelope.agent}</dd>
            </div>
            <div className="meta-row">
              <dt>outcome</dt>
              <dd><span className="pill">{envelope.outcome}</span></dd>
            </div>
            <div className="meta-row">
              <dt>occurred at</dt>
              <dd className="mono">{envelope.occurred_at}</dd>
            </div>
            <div className="meta-row">
              <dt>stored at</dt>
              <dd className="mono">{envelope.stored_at}</dd>
            </div>
            <div className="meta-row">
              <dt>digest</dt>
              <dd className="mono">{envelope.content_digest}</dd>
            </div>
            <div className="meta-row">
              <dt>size</dt>
              <dd className="mono">{fmtBytes(envelope.size_bytes)}</dd>
            </div>
            {envelope.envelope_version !== undefined && (
              <div className="meta-row">
                <dt>envelope version</dt>
                <dd className="mono">{envelope.envelope_version}</dd>
              </div>
            )}
            {envelope.actor_agent_ref !== undefined && (
              <div className="meta-row">
                <dt>via agent</dt>
                <dd className="mono">{envelope.actor_agent_ref}</dd>
              </div>
            )}
          </dl>

          {/* Collapsed by default: a package can be megabytes, and expanding is
              the reader's choice, not a side effect of opening a row. */}
          <details className="audit-package">
            <summary>package</summary>
            <pre className="audit-package-json mono">
              {JSON.stringify(data?.package ?? null, null, 2)}
            </pre>
          </details>

          <p className="note audit-transcript-note">{TRANSCRIPT_REF_NOTE}</p>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function AuditsTab() {
  const [filters, setFilters] = useState<AuditFilters>({});
  const [selected, setSelected] = useState<string | null>(null);
  /** The cursor for the page currently being fetched (undefined = first page). */
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  /** Rows from every page already paged past. The current page's rows are NOT
   *  in here — they come from `data` — so a refetch of the live page updates in
   *  place instead of appending a duplicate copy. */
  const [priorRows, setPriorRows] = useState<AuditEnvelopeView[]>([]);
  const { data, isLoading, isError, error, refetch, isFetching } = useAudits(filters, cursor);

  /** Set one filter, dropping it entirely when cleared — an empty string is not
   *  a filter value, and the hook would otherwise send `platform=`.
   *
   *  Any filter change RESETS pagination: rows accumulated under the old filter
   *  set do not belong under the new one, and the old cursor is a position in a
   *  different result set. */
  const setFilter = (key: keyof AuditFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    setCursor(undefined);
    setPriorRows([]);
  };

  /** Page forward: bank the rows now on screen, then request the next cursor. */
  const loadMore = () => {
    if (!data?.cursor) return;
    setPriorRows((prev) => [...prev, ...(data.audits ?? [])]);
    setCursor(data.cursor);
  };

  const controls = (
    <div className="card audits-controls">
      <label className="audits-control">
        <span className="audits-control-label">Platform</span>
        <input
          type="text"
          placeholder="any"
          value={filters.platform ?? ''}
          onChange={(e) => setFilter('platform', e.target.value)}
        />
      </label>
      <label className="audits-control">
        <span className="audits-control-label">Agent</span>
        <input
          type="text"
          placeholder="any"
          value={filters.agent ?? ''}
          onChange={(e) => setFilter('agent', e.target.value)}
        />
      </label>
      <label className="audits-control">
        <span className="audits-control-label">Outcome</span>
        <input
          type="text"
          placeholder="any"
          value={filters.outcome ?? ''}
          onChange={(e) => setFilter('outcome', e.target.value)}
        />
      </label>
      {/* since/until are sent verbatim as the API's parseable timestamps — plain
          text, not `datetime-local`, whose value is a LOCAL wall-clock string
          with no zone and would silently shift the window by the host offset. */}
      <label className="audits-control">
        <span className="audits-control-label">Since</span>
        <input
          type="text"
          placeholder="2026-08-16T00:00:00Z"
          value={filters.since ?? ''}
          onChange={(e) => setFilter('since', e.target.value)}
        />
      </label>
      <label className="audits-control">
        <span className="audits-control-label">Until</span>
        <input
          type="text"
          placeholder="2026-08-17T00:00:00Z"
          value={filters.until ?? ''}
          onChange={(e) => setFilter('until', e.target.value)}
        />
      </label>
    </div>
  );

  if (selected !== null) {
    return (
      <section className="audits-tab" aria-label="Audits">
        {controls}
        <AuditDetail packageId={selected} onBack={() => setSelected(null)} />
      </section>
    );
  }

  // Banked pages first, then the live page. Deduped by package_id so a replayed
  // cursor (or an overlapping page boundary) cannot paint the same package twice
  // or collide on the React key.
  const audits = Array.from(
    new Map(
      [...priorRows, ...(data?.audits ?? [])].map((a) => [a.package_id, a] as const),
    ).values(),
  );

  return (
    <section className="audits-tab" aria-label="Audits">
      {controls}

      {/* An error on page N is reported WITHOUT discarding pages 1..N-1: the
          rows already banked are still true, and blanking them would look like
          the filter stopped matching. */}
      {isError && (
        <p className="error" role="alert">
          Failed to load audit packages:{' '}
          {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      )}

      {isLoading && audits.length === 0 ? (
        <p className="note">Loading audit packages…</p>
      ) : isError && audits.length === 0 ? null : audits.length === 0 ? (
        // Deliberately NOT "no audit packages exist": the server filters after
        // reading, so an empty or short page says nothing about the whole store.
        <p className="note">No audit packages match these filters.</p>
      ) : (
        <table className="detail-table">
          <thead>
            <tr>
              <th>package</th>
              <th>platform</th>
              <th>agent</th>
              <th>outcome</th>
              <th>occurred at</th>
              <th className="detail-num">size</th>
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr
                key={a.package_id}
                className="audits-row"
                data-package-id={a.package_id}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelected(a.package_id)}
              >
                <td className="mono">{a.package_id}</td>
                <td>{a.platform}</td>
                <td>{a.agent}</td>
                <td><span className="pill">{a.outcome}</span></td>
                <td className="mono">{a.occurred_at}</td>
                <td className="detail-num mono">{fmtBytes(a.size_bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The cursor — not the page size — decides whether more exist. The server
          filters AFTER reading, so a SHORT page can still carry a cursor; gating
          this on `audits.length` would strand results behind a full page of
          post-filtered-away rows. */}
      {data?.cursor && (
        <div className="audits-more">
          <button type="button" className="btn" onClick={loadMore} disabled={isFetching}>
            {isFetching ? 'Loading more…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
